import React, { useMemo, useState } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { css } from '@emotion/core'
import { ButtonType } from '@/components/atoms/button'
import { DateField } from '@/components/molecules/date-field'
import { InvoiceType } from '@/models/sales-invoice'
import dayjs, { Dayjs } from 'dayjs'
import { TimePicker } from '@/components/atoms/time-picker'
import moment from 'moment'

interface Props {
  invoice: InvoiceType
  onClose: () => void
  onConfirm: (paymentAt: Dayjs) => void
}

export const ConfirmOfCompleteModal: React.FC<Props> = props => {
  const { t } = useTranslation()
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [paymentTime, setPaymentTime] = useState(dayjs().format('HH:mm'))

  const isCreatedBeforeCheckIn = useMemo(() => {
    return dayjs(props.invoice.createdAt).isBefore(dayjs(props.invoice.oldestCheckinDate))
  }, [props.invoice])

  const availableDateForPayment = useMemo(() => {
    return isCreatedBeforeCheckIn ? props.invoice.createdAt : props.invoice.oldestCheckinDate
  }, [isCreatedBeforeCheckIn])

  const isValid = useMemo(() => {
    return (
      dayjs(`${paymentDate} ${paymentTime}`).isAfter(dayjs(availableDateForPayment)) &&
      dayjs(`${paymentDate} ${paymentTime}`).isBefore(dayjs())
    )
  }, [paymentDate, paymentTime])

  return (
    <Modal customCss={modalStyle} containerCss={css({ display: 'flex' })}>
      <ModalHeader>{t('Set collection date and time')}</ModalHeader>
      <ModalBody>
        <div css={modalBodyStyle}>
          <div className="input-block">
            <div className="title">{t('Collected at')}</div>
            <div className="input-date-and-time">
              <style>{`
                .SingleDatePicker_picker { z-index: 11110; }
              `}</style>
              <DateField
                value={paymentDate}
                onChange={date => date && setPaymentDate(date.format('YYYY-MM-DD'))}
                isOutsideRange={day => day.isBefore(moment(availableDateForPayment), 'day') || day.isAfter(moment(), 'day')}
                appendToBody={true}
              />
              <TimePicker
                value={moment(paymentTime || '00:00', 'HH:mm') as any}
                onChange={value => {
                  setPaymentTime(value?.format('HH:mm'))
                }}
              />
            </div>
            {isCreatedBeforeCheckIn ? (
              <div className="description">{`${t('Input range')}: ${t('Invoice issue date')}(${dayjs(availableDateForPayment).format(
                t('MM-DD HH:mm'),
              )})~${t('current time')}`}</div>
            ) : (
              <div className="description">{`${t('Input range')}: ${t('Check-in date')}(${dayjs(availableDateForPayment).format(
                t('MM-DD'),
              )})~${t('current time')}`}</div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button buttonType={ButtonType.Secondary} height="38px" width="110px" marginRight={16} onClick={() => props.onClose()}>
          {t('Cancel')}
        </Button>
        <Button
          disabled={!isValid}
          buttonType={isValid ? ButtonType.Primary : ButtonType.PrimaryDisabled}
          height="38px"
          width="110px"
          onClick={() => {
            props.onConfirm(dayjs(`${paymentDate} ${paymentTime}`))
          }}
        >
          {t('Confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const modalStyle = css({
  width: 'fit-content',
  height: 'auto',
  margin: 'auto',
})

const modalBodyStyle = css({
  display: 'flex',
  height: '100%',
  padding: 32,
  border: '1px solid rgb(221, 221, 221)',
  borderRadius: 8,
  backgroundColor: 'rgb(255, 255, 255)',
  '.input-block': {
    '.title': {
      fontSize: 12,
      fontWeight: 'bold',
      letterSpacing: 0.6,
      color: 'rgb(103, 103, 103)',
      paddingBottom: 12,
    },
    '.input-date-and-time': {
      display: 'flex',
      gap: 8,
    },
    '.description': {
      paddingTop: 12,
      fontSize: 12,
      color: 'rgb(103, 103, 103)',
    },
  },
})
